import { useCallback, useMemo } from "react"
import { useWidget } from "../../../providers/widget"
import { ExploreWidgetConfig, VisitorFilter } from "../config"
import { useExplorer } from "../providers"
import { todayIso } from "./filters/flexDates"
import {
  applyVerdicts,
  rescueRangeLabel,
  rescueWindows,
  validRange,
  windowKey,
  type RescueWindow,
} from "./filters/pro/zeroResultsRescue"
import { useWindowAvailability } from "./filters/pro/useWindowAvailability"
import { boundsFiltering } from "./map/initialView"
import { RescueChipButton, rescueWindowWeeks } from "./ZeroResultsRescue"

/**
 * Module strings (flexible-search.js:737, :746, :799). `backTo` is a platform
 * addition asked for in #1265: the module offers only dismiss + other dates, so
 * the searched range itself was one click away only through the picker.
 */
export const ADJUSTED_NOTICE_I18N = {
  template: 'No homes were available for %1$s. Showing %2$s instead.',
  dismiss: 'Dismiss',
  otherAvailableDates: 'Other available dates:',
  backTo: 'Back to',
} as const

/** js:796 — the notice offers at most four other confirmed windows. */
const OTHER_DATES_MAX = 4

/**
 * "Dates adjusted" notice (#1265; module `syncAdjustedNotice`,
 * flexible-search.js:683-759 + `appendOtherAvailableDates`, :779-814).
 *
 * Rendered by PropertyGrid ABOVE a grid that has results, after a visitor took
 * a rescue chip: says plainly that the dates were adjusted, names both ranges,
 * offers the searched range back, and — once the original search's other
 * windows have been checked — the confirmed ones as pre-vetted alternatives
 * (unknown verdicts are omitted here, not kept: the whole point of the strip
 * is vetted suggestions). Dismiss removes the notice; the reducer retires the
 * marker on its own when the dates are cleared or revert to the searched range.
 */
export default function AdjustedDatesNotice({ filters }: { filters: Partial<VisitorFilter> }) {
  const { options, feedId } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter, setAdjustedFrom, rescuePivot } = useExplorer()
  const platformUuid = options.filter?.platform

  const from = state.adjustedFrom
  const checkin = state.visitorFilters.checkin ?? ''
  const checkout = state.visitorFilters.checkout ?? ''
  const show = Boolean(from)
    && options.searchbar.flexDates.enabled
    && !state.flexDates
    && validRange(checkin, checkout)
    && !(from!.checkin === checkin && from!.checkout === checkout)

  const { before, after } = rescueWindowWeeks(options)
  const today = todayIso()
  // The other-dates strip makes the same search-wide claim as the box, so it
  // sits out while an applied map area narrows the results (see rescueApplies).
  const bounded = boundsFiltering(state)
  const windows = useMemo(
    () => (show && from && !bounded
      ? rescueWindows(from.checkin, from.checkout, today, before, after)
        .filter((w) => !(w.checkin === checkin && w.checkout === checkout))
      : []),
    [show, from, bounded, today, before, after, checkin, checkout],
  )
  const { verdictFor } = useWindowAvailability(feedId, filters, windows, platformUuid, show && windows.length > 0)

  const back = useCallback(() => {
    if (!from) return
    // Reverting to the searched range retires the marker in the reducer.
    setFilter({ checkin: from.checkin, checkout: from.checkout })
  }, [from, setFilter])

  const pick = useCallback((w: RescueWindow) => {
    // Same transition as the box's pivot; keeps the searched dates as the
    // notice's reference (js:776-777).
    rescuePivot({ checkin: w.checkin, checkout: w.checkout, from })
  }, [rescuePivot, from])

  if (!show || !from) return null

  const fromLabel = rescueRangeLabel(from.checkin, from.checkout)
  const text = ADJUSTED_NOTICE_I18N.template
    .replace('%1$s', fromLabel)
    .replace('%2$s', rescueRangeLabel(checkin, checkout))
  // Confirmed windows land as their (usually cached) probes answer; the strip
  // renders as soon as the first one does.
  const confirmed = applyVerdicts(windows, verdictFor).chips.filter((c) => c.verified).slice(0, OTHER_DATES_MAX)

  return (
    <div className="hfse-flex-adjusted" role="status">
      <div className="hfse-flex-adjusted-main">
        <p className="hfse-flex-adjusted-text">{text}</p>
        <button type="button" className="hfse-flex-adjusted-back" onClick={back}>
          {ADJUSTED_NOTICE_I18N.backTo} {fromLabel}
        </button>
        {confirmed.length > 0 && (
          <div className="hfse-flex-adjusted-alts">
            <span className="hfse-flex-adjusted-alts-heading">{ADJUSTED_NOTICE_I18N.otherAvailableDates}</span>
            {confirmed.map((chip) => (
              <RescueChipButton key={windowKey(chip)} chip={chip} onPick={pick} />
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        className="hfse-flex-adjusted-dismiss"
        aria-label={ADJUSTED_NOTICE_I18N.dismiss}
        onClick={() => setAdjustedFrom(null)}
      >
        &times;
      </button>
    </div>
  )
}
